/* fc-sticky-buy.js — pinned product-page buy bar (snippets/fc-sticky-buy.liquid).

   WHY
   87% of shoppers are on phones, and on an iPhone 13/14 the product page's Add to cart
   starts right at the bottom edge of the screen. Below 990px this bar pins title, price and
   Add to cart to the bottom of the viewport while the page's own button is out of view.

   RULES
   - Never adds on its own. Its button submits the MAIN product form (same variant id,
     quantity and line-item properties, same assets/product-form.js path). If that form has
     a required field that is empty, it scrolls to and focuses that field instead.
   - Follows the main button: sold out / unavailable / loading are mirrored from it, so any
     state Cello puts on that button (product-info.js toggleSubmitButton) shows here too.
     Price comes from the per-variant JSON rendered in the snippet.
   - Visible only below 990px and only while the main submit button is off screen
     (IntersectionObserver). Hidden while a drawer or modal holds the page
     (body.overflow-hidden*), see fc-sticky-buy.css.
   - While visible, sets --fc-sticky-buy-h on <body>; fc-sticky-buy.css lifts the Party
     Planner chat launcher (the app embed's fixed .shop-ai-chat-container) by that height.
   - The [−] N in cart [+] counter is driven by assets/fc-cart-qty.js: this element is a
     `data-fc-qty-slot` pointing at the main form. */
(function () {
  'use strict';
  if (window.FCStickyBuy) return;

  var MQ = window.matchMedia('(max-width: 989px)');
  var queued = false;

  function textOf(button) {
    var span = button.querySelector('span');
    return ((span || button).textContent || '').trim();
  }

  function firstInvalid(form) {
    var els = form.elements;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.willValidate && !el.checkValidity()) return el;
    }
    return null;
  }

  function init(bar) {
    if (bar.classList.contains('fc-sticky-buy--ready')) return;
    bar.classList.add('fc-sticky-buy--ready');

    var section = bar.getAttribute('data-section');
    var form = document.getElementById(bar.getAttribute('data-fc-qty-form'));
    var mainBtn = document.getElementById(bar.getAttribute('data-main-button'));
    if (!form || !mainBtn) {
      bar.remove(); // no buy buttons block on this template: nothing to pin
      return;
    }

    // One bar per section. A product swap can re-render main-product; drop the old copy.
    document.querySelectorAll('[data-fc-sticky-buy]').forEach(function (other) {
      if (other !== bar && other.getAttribute('data-section') === section) other.remove();
    });
    document.body.appendChild(bar);
    document.body.classList.add('fc-sticky-buy-page');

    var add = bar.querySelector('[data-fc-sticky-add]');
    var label = bar.querySelector('[data-fc-sticky-label]');
    var price = bar.querySelector('[data-fc-sticky-price]');
    var addText = (window.variantStrings && window.variantStrings.addToCart) || textOf(add);
    var variants = {};
    try {
      variants = JSON.parse(bar.querySelector('[data-fc-sticky-variants]').textContent);
    } catch (e) {
      variants = {};
    }

    var mainInView = true;
    var shown = false;

    function variantId() {
      var input = form.querySelector('input[name="id"]');
      return input ? String(input.value) : '';
    }

    // Wear the main button's colours (they follow the colour scheme / palette); the
    // counter reads the same variables. Sampled when the bar appears, when the main
    // button is off screen and therefore not hovered.
    function mirrorColors() {
      // Never sample a hover colour (a tapped button keeps :hover on touch) or a transition.
      var running = mainBtn.getAnimations
        ? mainBtn.getAnimations().some(function (a) {
            return a.playState === 'running';
          })
        : false;
      if (running || mainBtn.matches(':hover')) return;
      var cs = window.getComputedStyle(mainBtn);
      bar.style.setProperty('--fc-qty-fg', cs.color);
      bar.style.setProperty('--fc-qty-bg', cs.backgroundColor);
      bar.style.setProperty(
        '--fc-qty-border',
        parseFloat(cs.borderTopWidth) > 0 ? cs.borderTopColor : cs.backgroundColor
      );
    }

    function syncFromMain() {
      var disabled = mainBtn.disabled;
      add.disabled = disabled;
      var next = disabled ? textOf(mainBtn) || label.textContent : addText;
      if (label.textContent !== next) label.textContent = next;
      var busy = mainBtn.classList.contains('loading') || mainBtn.getAttribute('aria-disabled') === 'true';
      add.classList.toggle('is-loading', busy && !disabled);
      add.setAttribute('aria-disabled', busy ? 'true' : 'false');

      var v = variants[variantId()];
      if (v) {
        if (price.textContent !== v.price) price.textContent = v.price;
        price.hidden = false;
      } else {
        price.hidden = true; // unavailable option combination
      }
    }

    function update() {
      var show = MQ.matches && !mainInView;
      if (show === shown) return;
      shown = show;
      if (show) mirrorColors();
      bar.classList.toggle('is-visible', show);
      document.body.classList.toggle('fc-sticky-buy-on', show);
      if (show) document.body.style.setProperty('--fc-sticky-buy-h', bar.offsetHeight + 'px');
    }

    // The main button counts as "in view" only once it clears the bar's own strip, so the
    // two never overlap.
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          mainInView = entry.isIntersecting;
        });
        update();
      },
      { rootMargin: '0px 0px -' + Math.max(bar.offsetHeight, 64) + 'px 0px' }
    );
    io.observe(mainBtn);
    // A message under the bar changes its height; keep the chat lift in step.
    if (window.ResizeObserver) {
      new ResizeObserver(function () {
        if (shown) document.body.style.setProperty('--fc-sticky-buy-h', bar.offsetHeight + 'px');
      }).observe(bar);
    }
    if (MQ.addEventListener) MQ.addEventListener('change', update);
    else if (MQ.addListener) MQ.addListener(update);

    new MutationObserver(syncFromMain).observe(mainBtn, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });
    form.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'id') syncFromMain();
    });
    // A refused add (e.g. 422 "You can only add 5…") is written by product-form.js into the
    // MAIN form's error box, which is off screen whenever this bar shows. Repeat it here.
    var errBox = bar.querySelector('.fc-sticky-buy__error');
    var errText = errBox && errBox.querySelector('.product-form__error-message');

    function showError(event) {
      if (!shown || !errBox || !event) return;
      if (String(event.productVariantId) !== variantId()) return;
      var text = [event.errors, event.message].filter(function (t) {
        return typeof t === 'string' && t.trim();
      })[0];
      errText.textContent = text || (window.cartStrings && window.cartStrings.error) || '';
      errBox.hidden = false;
    }

    function clearError() {
      if (errBox) errBox.hidden = true;
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, syncFromMain);
      subscribe(PUB_SUB_EVENTS.cartError, showError);
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        // The counter's own updates are excluded: it clears this box on every tap and may
        // just have written "Only N left" into it.
        if (!event || event.source !== 'fc-cart-qty') clearError();
      });
    }

    add.addEventListener('click', function () {
      clearError();
      if (add.disabled || add.getAttribute('aria-disabled') === 'true') return;
      var invalid = firstInvalid(form);
      if (invalid) {
        // e.g. a required pickup field: take the shopper to it rather than fail silently.
        invalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
        setTimeout(function () {
          invalid.focus({ preventScroll: true });
          if (invalid.reportValidity) invalid.reportValidity();
        }, 350);
        return;
      }
      if (typeof form.requestSubmit === 'function') form.requestSubmit(mainBtn);
      else mainBtn.click();
    });

    syncFromMain();
    update();
    setTimeout(mirrorColors, 800); // after Cello's load-time button transition
    if (window.FCCartQty) window.FCCartQty.paint();
  }

  function initAll() {
    document.querySelectorAll('[data-fc-sticky-buy]').forEach(init);
  }

  window.FCStickyBuy = { init: initAll };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  // Theme editor section reloads and product swaps render a fresh bar.
  new MutationObserver(function () {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      if (document.querySelector('[data-fc-sticky-buy]:not(.fc-sticky-buy--ready)')) initAll();
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
